import React, { useMemo, useRef, useEffect, memo } from "react";
import * as echarts from "echarts";
import { Box, useTheme } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import type { SxProps } from "@mui/system";
import munGeoJson from "../../../assets/topoJson/geojs-100-mun-fixed.json";
import { VectorMapConfig } from "./types";
import { getColorsArray } from "../../../utils/getColorsPallet";

const VectorMap: React.FC<VectorMapConfig> = ({
  data,
  keys,
  indexBy,
  colors,
}) => {
  const theme = useTheme();
  const sx = useMemo(() => style(theme), [theme]);
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  const formattedData = useMemo(() => {
    return (data ?? [])
      .map((d) => ({
        id: d[indexBy as keyof typeof d] as string,
        value: Number(d[keys as keyof typeof d] as number) || 0,
      }))
      .sort((a, b) => a.value - b.value);
  }, [data, indexBy, keys]);

  const palette = useMemo(() => {
    if (colors?.scheme) {
      return getColorsArray(5, colors, theme.palette.mode === "dark").reverse();
    }
    return Object.keys(theme.palette.cromatic.orange)
      .map(
        (k) =>
          theme.palette.cromatic.orange[
            k as keyof typeof theme.palette.cromatic.orange
          ],
      )
      .slice(0, -1);
  }, [colors, theme]);

  useEffect(() => {
    if (!chartRef.current) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(
        chartRef.current,
        theme.palette.mode === "dark" ? "dark" : undefined,
        {
          renderer: "canvas",
        },
      );
    }

    const myChart = chartInstance.current;

    myChart.resize();
    myChart.showLoading();

    const availableIds = new Set(formattedData.map((d) => d.id));
    const allFeatures = (munGeoJson as any).features;

    let minLng = Infinity,
      minLat = Infinity,
      maxLng = -Infinity,
      maxLat = -Infinity;

    const updateBounds = (coords: any[]) => {
      const traverse = (c: any) => {
        if (Array.isArray(c) && typeof c[0] === "number") {
          const [lng, lat] = c;
          minLng = Math.min(minLng, lng);
          maxLng = Math.max(maxLng, lng);
          minLat = Math.min(minLat, lat);
          maxLat = Math.max(maxLat, lat);
        } else if (Array.isArray(c)) {
          c.forEach(traverse);
        }
      };
      traverse(coords);
    };

    const featuresWithData = allFeatures.filter((f: any) =>
      availableIds.has(f.properties.id),
    );

    if (featuresWithData.length > 0) {
      featuresWithData.forEach((f: any) =>
        updateBounds(f.geometry.coordinates),
      );
    } else {
      minLng = -55;
      maxLng = -45;
      minLat = -15;
      maxLat = -10;
    }

    const lngSpan = maxLng - minLng || 0.1;
    const latSpan = maxLat - minLat || 0.1;

    const buffer = 1.5;
    const contextBounds = {
      w: minLng - lngSpan * buffer,
      e: maxLng + lngSpan * buffer,
      s: minLat - latSpan * buffer,
      n: maxLat + latSpan * buffer,
    };

    const optimizedFeatures = allFeatures.filter((f: any) => {
      if (availableIds.has(f.properties.id)) return true;

      let c = f.geometry.coordinates;
      while (Array.isArray(c[0])) c = c[0];
      const [lng, lat] = c;

      return (
        lng >= contextBounds.w &&
        lng <= contextBounds.e &&
        lat >= contextBounds.s &&
        lat <= contextBounds.n
      );
    });

    const mapName = "BrazilOptimizedSubset";
    echarts.registerMap(mapName, {
      type: "FeatureCollection",
      features: optimizedFeatures,
    });

    const paddingX = lngSpan * 0.1;
    const paddingY = latSpan * 0.1;
    const boundingCoords = [
      [minLng - paddingX, minLat - paddingY],
      [maxLng + paddingX, maxLat + paddingY],
    ];

    const idToNameMap: Record<string, string> = {};
    optimizedFeatures.forEach((f: any) => {
      idToNameMap[f.properties.id] = f.properties.name;
    });

    const chartData = formattedData
      .filter((d) => idToNameMap[d.id])
      .map((d) => ({
        name: idToNameMap[d.id],
        value: d.value,
        originalId: d.id,
      }));

    myChart.hideLoading();

    const maxValue = Math.max(...chartData.map((d) => d.value), 10);

    const option = {
      backgroundColor: "transparent",
      tooltip: {
        trigger: "item",
        formatter: (params: any) => {
          const value =
            isNaN(params.value) || params.value == null ? 0 : params.value;
          return `${params.name}: ${value}`;
        },
        confine: true,
      },
      visualMap: {
        orient: "horizontal",
        left: "center",
        min: 0,
        max: maxValue,
        inRange: {
          color: palette,
        },
        calculable: true,
        textStyle: {
          color: theme.palette.text.primary,
        },
        itemHeight: 300,
        itemWidth: 10,
        show: true,
      },
      series: [
        {
          id: "mainData",
          type: "map",
          map: mapName,
          roam: false,
          scaleLimit: { min: 1, max: 10 },
          boundingCoords: boundingCoords,
          label: {
            show: false,
          },
          itemStyle: {
            areaColor: theme.palette.cromatic.grey.main,
            borderColor: theme.palette.cromatic.grey.light,
            borderWidth: 0.5,
          },
          emphasis: {
            itemStyle: {
              areaColor: theme.palette.action.hover,
            },
            label: {
              show: false,
            },
          },
          data: chartData,
        },
      ],
    };

    myChart.setOption(option, { notMerge: false, lazyUpdate: true });

    const handleResize = () => myChart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [formattedData, theme, palette]);

  return (
    <Box sx={sx.container}>
      <Box sx={sx.mapContainer}>
        <div
          ref={chartRef}
          style={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        />
      </Box>
    </Box>
  );
};

const style = (theme: Theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%", // Fill the parent
    flexGrow: 1,
    overflow: "hidden",
    alignItems: "center",
    bgcolor: theme.palette.background.card,
    borderRadius: 2,
    p: 1,
  } as SxProps<Theme>,
  mapContainer: {
    flex: 1,
    width: "100%",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  } as SxProps<Theme>,
});

export default memo(VectorMap);
